import * as countryCodes from 'country-codes-list';

type CountryData = {
  countryNameEn: string;
  countryNameLocal: string;
  countryCode: string;
  currencyCode: string;
  currencyNameEn: string;
  tinType: string;
  tinName: string;
  officialLanguageCode: string;
  officialLanguageNameEn: string;
  officialLanguageNameLocal: string;
  countryCallingCode: string;
  areaCodes?: any[];
  region: string;
  flag: string;
};

const countryDialCodeMap = new Map<string, string>();

export const getDialCodeByCountry = (countryCode: string): string | null => {
  if (countryDialCodeMap.has(countryCode)) {
    const cached = countryDialCodeMap.get(countryCode)!;
    return cached || null;
  }
  const all = countryCodes.all() as CountryData[];
  const country = all.find((c: CountryData) => c.countryCode.toUpperCase() === countryCode.toUpperCase());
  const dialCode = country?.countryCallingCode ? `+${country.countryCallingCode}` : null;
  countryDialCodeMap.set(countryCode, dialCode ?? '');
  return dialCode;
};

type FormatE164PhoneNumberParams = {
  phoneNumber: string;
  countryCode: string;
};

/**
 * Remove spaces and dashes from phone number
 * e.g., "+82 10-2544-1586" → "+821025441586"
 */
const sanitizePhoneNumber = (phone: string): string => {
  return phone.replace(/[\s\-]/g, '');
};

/**
 * Formats phone number to E.164 format
 * - Returns empty string for empty input
 * - Sanitizes input (removes spaces, dashes)
 * - Returns as-is if already starts with '+' (after sanitization)
 * - Removes leading zero (common in local formats) before adding dial code
 * - Falls back to sanitized input if country code not found
 */
export const formatE164PhoneNumber = ({ phoneNumber, countryCode }: FormatE164PhoneNumberParams): string => {
  if (!phoneNumber) {
    return '';
  }

  // Trim and sanitize (remove spaces, dashes)
  const sanitized = sanitizePhoneNumber(phoneNumber.trim());

  if (sanitized.startsWith('+')) {
    return sanitized;
  }

  const phoneWithoutLeadingZero = sanitized.startsWith('0') ? sanitized.slice(1) : sanitized;
  const dialCode = getDialCodeByCountry(countryCode) ?? '';

  return dialCode ? `${dialCode}${phoneWithoutLeadingZero}` : sanitized;
};
